import { useFormUtils } from "@/app/hooks/useFormUtils";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { Combobox, Transition, Portal } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { isObject } from "lodash";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { BsFillCheckCircleFill } from "react-icons/bs";
import { IoMdArrowDropdown } from "react-icons/io";
import { Tooltip } from "react-tooltip";
// import FormFieldErrors from "../../Reusable/FormFieldErrors";
// import { ISearchableSelectMenu, ISelectMenuItemData, SelectMenuItems } from "../../interface";
import FormFieldErrors from "@/app/components/ui/Reusable/FormFieldErrors";
import { ISearchableSelectMenu, ISelectMenuItemData, SelectMenuItems } from "@/app/components/ui/interface";

const PoSelectMenu: React.FC<ISearchableSelectMenu> = ({
  fieldError,
  placeholder,
  defaultSelected,
  className,
  itemsClassName,
  defaultSelectId,
  asButton,
  renderAsButton,
  withReset,
  appendQueryInOptions,
  showDropdownIcon,
  inputContainerClassName,
  errorMessages,
  optionRenderVariant = "DEFAULT",
  showDropdownWhenNoData,
  showTypedErrors,
  rawSelectedString,
  dropdownPosition = "bottom",
  showTooltips,
  listZIndex,
  register,
  disableSearch,
  isMultiSelect,
  onSelectItem,
  listBoxClassName,
  onInputChange,
  onLikeTextVariantInputChange,
  addAction,
  ...props
}) => {
  const { deduceFormFieldErrors, isFormFieldValid } = useFormUtils();
  const [selected, setSelected] = useState<ISelectMenuItemData | null>(null);
  const [query, setQuery] = useState<string>("");
  const [filteredDataItems, setFilteredDataItems] = useState<SelectMenuItems>([]);
  const [isValid, setIsValid] = useState<boolean>(false);

  const selectMenuReducer = useSelectMenuReducer;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputContainerRef = useRef<HTMLDivElement | null>(null);
  const [positionTick, setPositionTick] = useState<number>(0);
  const tickPosition = () => {
    // schedule after layout to ensure accurate rects
    requestAnimationFrame(() => setPositionTick((t) => t + 1));
  };

  useEffect(() => {
    const handler = () => setPositionTick((t) => t + 1);
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, []);

  const computeDropdownStyle = useMemo(() => {
    const anchorEl = inputContainerRef.current ?? containerRef.current;
    if (!anchorEl) return {} as React.CSSProperties;
    const rect = anchorEl.getBoundingClientRect();
    const viewportPadding = 8;
    const maxAllowedWidth = Math.max(0, window.innerWidth - viewportPadding * 2);
    const computedWidth = Math.min(rect.width, maxAllowedWidth);
    let computedLeft = rect.left;
    if (computedLeft + computedWidth > window.innerWidth - viewportPadding) {
      computedLeft = Math.max(viewportPadding, window.innerWidth - viewportPadding - computedWidth);
    }
    if (computedLeft < viewportPadding) {
      computedLeft = viewportPadding;
    }

    const style: React.CSSProperties = {
      position: "fixed",
      left: computedLeft,
      width: computedWidth,
      maxWidth: maxAllowedWidth,
      zIndex: 999999,
    };
    if (dropdownPosition === "top") {
      style.bottom = window.innerHeight - rect.top;
    } else {
      style.top = rect.bottom;
    }
    return style;
  }, [positionTick, dropdownPosition]);

  const reducedErrorMessages = deduceFormFieldErrors(errorMessages, fieldError);
  const invalid: boolean = isFormFieldValid(reducedErrorMessages);

  useEffect(() => {
    if (!invalid) {
      setIsValid(false);
    } else {
      setIsValid(true);
    }
  }, [invalid]);

  useEffect(() => {
    if (selected) {
      setIsValid(true);
    }
  }, [selected]);

  useEffect(() => {
    if (defaultSelected === undefined) {
      setSelected(null);
    } else {
      setSelected(defaultSelected);
    }
  }, [defaultSelected]);

  useEffect(() => {
    if (props?.defaultSelectedItemForAPISearch && isObject(props?.defaultSelectedItemForAPISearch)) {
      // Don't do API search rather just load selected
      if (query?.length === 0 && props?.withAPISearch) {
        setSelected(props?.defaultSelectedItemForAPISearch);
      }
    }
  }, [props?.defaultSelectedItemForAPISearch]);

  useEffect(() => {
    if (!defaultSelectId && props.withAPISearch) return;
    if (props?.selectItems?.length === 0) return;
    if (defaultSelectId) {
      setSelected(props?.selectItems?.filter((i) => i.id === defaultSelectId)[0]);
    }
  }, [defaultSelectId]);

  useEffect(() => {
    if (props?.withAPISearch && isObject(props?.withAPISearch)) {
      if (query?.length >= 3) {
        props.withAPISearch?.apiCallback(query).then((res: Array<any>) => {
          if (res?.length > 0) {
            const items = selectMenuReducer(
              res,
              props?.withAPISearch.reducerTitleKey,
              props?.withAPISearch?.reducerIdKey,
            );

            setFilteredDataItems(items);
          }
        });
      }
    } else {
      setFilteredDataItems([]);
    }
  }, [query]);

  useEffect(() => {
    if (!props?.withAPISearch) {
      const items =
        query === ""
          ? props?.selectItems
          : props?.selectItems.filter((item) =>
              item?.title?.toLowerCase()?.replace(/\s+/g, "")?.includes(query.toLowerCase()?.replace(/\s+/g, "")),
            );

      setFilteredDataItems(items);
    } else {
      setFilteredDataItems([]);
    }

    if (appendQueryInOptions) {
      if (onLikeTextVariantInputChange) {
        onLikeTextVariantInputChange(query);
      }
    }
  }, [query, props?.selectItems]);

  const showFieldErrors = showTypedErrors && showTypedErrors === true ? true : showTypedErrors === false ? false : true;

  const handleReset = () => {
    setSelected(null);
    if (onSelectItem) {
      onSelectItem(null);
    }
  };

  return (
    <div style={props?.style ?? {}} className={clsx("relative", props?.containerClassName ?? "")} ref={containerRef}>
      <Combobox
        disabled={props?.disabled ?? false}
        value={selected}
        onChange={(item) => {
          setSelected(item);
          if (onSelectItem) {
            onSelectItem(item);
          }
        }}
        nullable
      >
        {({ open }) => {
          return (
            <>
              {open && props?.variant && props?.variant === "LIKE_TEXT" && filteredDataItems?.length > 0 && (
                <div className="top-[35px] left-2 absolute bg-white shadow-md p-3 rounded-sm rotate-45"></div>
              )}
              <div className="relative w-full">
                {props?.label && (
                  <span className="-top-4 left-1 absolute text-gray-500  text-[10px] sm:text-xs">{props?.label}</span>
                )}
                <div
                  className={clsx(
                    "relative border-gray-300 bg-none border rounded-lg w-full text-left sm:text-sm cursor-default overflow-hidden focus:outline-none",
                    inputContainerClassName,
                    props?.variant &&
                      props?.variant === "LIKE_TEXT" &&
                      "border-none outline-none ring-0 bg-transparent",
                    props?.variant && props?.variant === "LIKE_INPUT" && "bg-white",
                    asButton && "border-none outline-none ring-0 bg-none bg-transparent",
                  )}
                  ref={inputContainerRef}
                  onClick={tickPosition}
                  onFocus={tickPosition}
                >
                  {withReset && selected && (
                    <span onClick={handleReset} className="top-1/2 right-7 absolute -translate-y-1/2 cursor-pointer">
                      <XMarkIcon className="w-4 h-4 text-red-500" />
                    </span>
                  )}
                  <input {...register} hidden />
                  {asButton && asButton === true ? (
                    <Combobox.Button>{renderAsButton}</Combobox.Button>
                  ) : disableSearch && disableSearch === true ? (
                    <>
                      <Combobox.Button className="flex justify-start items-center w-full" onClick={tickPosition}>
                        <Combobox.Input
                          readOnly
                          autoComplete="false"
                          autoCorrect="false"
                          placeholder={placeholder}
                          displayValue={(item: ISelectMenuItemData): string => {
                            return item?.title;
                          }}
                          className={clsx(
                            `w-full  text-[10px] sm:text-xs  font-medium  rounded-lg py-2 pl-3 pr-10 leading-5  focus:ring-0 ${className}`,
                            isValid
                              ? "text-black border-none outline-none font-medium "
                              : "text-red-600  border border-red-500 outline-red-500 ::place  .placeholder-red-500",
                            props?.variant &&
                              props?.variant === "LIKE_INPUT" &&
                              "text-gray-500 border-none outline-none font-medium ",
                            props?.variant &&
                              props?.variant === "LIKE_TEXT" &&
                              "border-t-0 border-l-0 border-r-0  outline-none cursor-pointer ring-0 border-b border-b-red-500 bg-transparent",
                            props?.disabled && props?.disabled === true && "bg-gray-200",
                          )}
                          onChange={(event) => {
                            setQuery(event.target.value);
                          }}
                        />
                      </Combobox.Button>
                    </>
                  ) : (
                    <Combobox.Button className="w-full" onClick={tickPosition}>
                      <Combobox.Input
                        autoComplete="off"
                        autoCorrect="false"
                        className={clsx(
                          `w-full  text-[10px] sm:text-xs font-medium  rounded-lg py-2 pl-3 pr-10 leading-5  focus:ring-0 ${className}`,
                          isValid
                            ? "text-black border-none outline-none font-medium "
                            : "text-red-600  border border-red-500 outline-red-500 placeholder-red-400 ",
                          props?.variant &&
                            props?.variant === "LIKE_INPUT" &&
                            "text-gray-500 border-none outline-none font-medium ",
                          props?.variant &&
                            props?.variant === "LIKE_TEXT" &&
                            "border-t-0 border-l-0 border-r-0  outline-none cursor-pointer ring-0 border-b border-b-red-500 ",
                          props?.disabled && props?.disabled === true && "bg-gray-200",
                        )}
                        placeholder={placeholder}
                        displayValue={(item: ISelectMenuItemData): string => {
                          return item?.title;
                        }}
                        onChange={(event) => {
                          setQuery(event.target.value);
                          if (onInputChange) {
                            onInputChange(event.target.value);
                          }
                        }}
                      />
                    </Combobox.Button>
                  )}
                  {showDropdownIcon && showDropdownIcon === true ? (
                    <Combobox.Button
                      className="right-0 absolute inset-y-0 flex items-center pr-2"
                      onClick={tickPosition}
                    >
                      <IoMdArrowDropdown className="w-5 h-5 text-gray-400" aria-hidden="true" />
                    </Combobox.Button>
                  ) : (
                    <></>
                  )}
                </div>
                {showFieldErrors && !isValid && (
                  <div className="absolute">
                    <FormFieldErrors errors={reducedErrorMessages} />
                  </div>
                )}
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => setQuery("")}
                >
                  <Portal>
                    <Combobox.Options
                      onWheel={(e) => {
                        e.stopPropagation();
                      }}
                      onTouchMove={(e) => {
                        e.stopPropagation();
                      }}
                      style={computeDropdownStyle}
                      className={clsx(
                        filteredDataItems?.length > 0 &&
                          "mt-1 overflow-auto overscroll-contain rounded-md bg-white py-1 text-base shadow-lg focus:outline-none max-h-60 sm:text-sm",

                        listBoxClassName ? listBoxClassName : "",
                        listZIndex ? listZIndex : "z-[999999]",
                      )}
                    >
                      {props?.withDropDownSearch &&
                        props?.withDropDownSearch === true &&
                        filteredDataItems?.length > 0 && (
                          <Combobox.Option
                            value={null}
                            className={"w-full pl-2"}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <div className="flex justify-start items-center bg-white ">
                              <span>
                                <MagnifyingGlassIcon className="w-3 h-3" />
                              </span>
                              <input
                                onChange={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setQuery(e.target.value);
                                }}
                                className="pr-4 pl-2  border-t-0 border-r-0 border-b border-b-gray-200 border-l-0 ring-0 focus:ring-0 w-full text-black  text-[10px] sm:text-xs outline-none focus:outline-none"
                                type="text"
                                placeholder="Search withing options"
                              />
                            </div>
                          </Combobox.Option>
                        )}
                      {filteredDataItems?.length === 0 ? (
                        <div className="px-4 py-2 w-full text-gray-700 cursor-default select-none">
                          {props?.dropdownNoDataContent ?? (
                            <span className="block text-[10px] sm:text-xs">
                              {props?.withAPISearch && query.length < 3
                                ? `Type ${3 - Math.min(query.length, 3)} more characters to search`
                                : "No Data"}
                            </span>
                          )}
                        </div>
                      ) : (
                        filteredDataItems?.map((item: ISelectMenuItemData) => (
                          <Combobox.Option
                            key={item.id}
                            className={({ selected }) =>
                              clsx(
                                "relative flex justify-between items-center py-2 pl-2 font-medium text-center  text-[10px] sm:text-xs cursor-default select-none",
                                selected
                                  ? optionRenderVariant === "DEFAULT"
                                    ? "bg-gray-800 hover:bg-gray-800 text-white font-medium "
                                    : ""
                                  : optionRenderVariant === "DEFAULT"
                                    ? "text-gray-800 hover:bg-gray-800 hover:text-white"
                                    : "text-gray-800 hover:bg-gray-100 hover:text-gray-800",
                                itemsClassName && itemsClassName !== "" ? itemsClassName : "",
                              )
                            }
                            onClick={() => setSelected(item)}
                            data-tooltip-place="right"
                            data-tooltip-id={`option-tooltip:${register?.name}`}
                            data-tooltip-content={item.title}
                            value={item}
                          >
                            {({ selected, active }) => (
                              <>
                                {isMultiSelect && isMultiSelect === true && (
                                  <input
                                    id="comments"
                                    aria-describedby="comments-description"
                                    name="comments"
                                    type="checkbox"
                                    className="border-gray-300 rounded focus:ring-indigo-600 w-4 h-4 text-indigo-600"
                                  />
                                )}
                                {optionRenderVariant === "DEFAULT" ? (
                                  <span
                                    className={`block text-left truncate ${selected ? "font-medium " : "font-normal"}`}
                                  >
                                    {item.title} {props?.extraInfoNode && <span>{props?.extraInfoNode}</span>}
                                  </span>
                                ) : (
                                  <div className="relative flex items-center w-[92%] h-16 bg-teal-100 border border-teal-300 rounded-lg overflow-hidden">
                                    <div className="absolute left-0 w-4 h-full bg-white"></div>
                                    <div className="absolute left-0 w-8 h-full">
                                      <div className="absolute top-0 left-4 w-8 h-8 bg-white rounded-full -translate-y-1/2"></div>
                                      <div className="absolute bottom-0 left-4 w-8 h-8 bg-white rounded-full translate-y-1/2"></div>
                                    </div>
                                    <div className="flex flex-col ml-12 pr-4">
                                      <span className="text-left font-semibold text-teal-800 text-sm w-32 text-wrap truncate">
                                        {item.title}
                                      </span>
                                    </div>
                                    {selected && (
                                      <div className="absolute right-1 -top-5  w-4 h-full  flex items-center justify-center">
                                        <BsFillCheckCircleFill className="w-4 h-4 text-teal-600" />
                                      </div>
                                    )}
                                  </div>
                                )}
                                {optionRenderVariant === "DEFAULT" && selected ? (
                                  <div className="flex justify-start items-center text-left">
                                    <span
                                      className={`inset-y-0 text-left left-0 flex items-center pl-3 ${
                                        active ? "text-white" : "text-blue-600"
                                      }`}
                                    ></span>
                                    <CheckIcon className="mr-4 w-4 h-4" />
                                  </div>
                                ) : null}
                              </>
                            )}
                          </Combobox.Option>
                        ))
                      )}

                      {addAction && filteredDataItems?.length >= 0 && (
                        <div className="p-2">
                          <hr />
                          <Combobox.Button
                            className="text-[#0068FF] cursor-pointer"
                            onClick={() => addAction?.callBack(query)}
                          >
                            {addAction?.title} {query && <span>{`"${query}"`}</span>}
                          </Combobox.Button>
                        </div>
                      )}
                    </Combobox.Options>
                  </Portal>
                </Transition>
              </div>
            </>
          );
        }}
      </Combobox>
      {/* {showTooltips && showTooltips === true && ( */}
      <Tooltip id={`option-tooltip:${register?.name}`} style={{ zIndex: 10, fontSize: "10px" }} />
      {/* )} */}
    </div>
  );
};

export default PoSelectMenu;
